import {
  useEffect,
  useState,
  ChangeEvent,
  useContext,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useHistory } from "react-router-dom";
import { useForm, SubmitHandler, UseFormRegister } from "react-hook-form";
import { format, parse, differenceInYears, isValid, isFuture } from "date-fns";

import Input from "@/components/ui/Input";
import Checkbox from "@/components/checkbox/Checkbox";
import ErrorMessage from "@/components/error-message/error-message";
import Form from "@/components/Form/Form";
import Header from "@/components/header/Header";
import Datepicker from "@/components/ui/date-picker";
import UsernameInput from "@/components/username-input/UsernameInput";

import { FormError } from "@/models/continente-credentials/common/FormError";
import { CardCreation } from "@/models/continente-credentials/mobile/CardCreation";
import { PhoneNumberAssociationResponse } from "@/models/continente-credentials/mobile/PhoneNumberAssociationResponse";

import { UserContext } from "@/contexts/continente-credentials/UserContext";

import { cardCreationService } from "@/services/continente-credentials/mobile/CardCreationService";

import { validatePortuguesePhoneNumber } from "@/utils/validatePortuguesePhoneNumber";
import { Button } from "@/components/ui/button";
import { tryCatch } from "@/utils/try-catch";
import { useApiResponseHandler } from "@/hooks/useApiResponseHandler";
import { useGoToFaq } from "@/hooks/useGoToFaq";

type Form = CardCreation & FormError;

export default function LoyaltyCardCreation() {
  const { t } = useTranslation();
  const goToFaq = useGoToFaq();
  const location = useLocation<PhoneNumberAssociationResponse>();
  const [labelBirthdateVisible, setLabelBirthdateVisible] =
    useState<boolean>(true);
  const [name, setName] = useState<string>("");
  const { handleResponse } = useApiResponseHandler();

  const [isEmailAlreadySet, setIsEmailAlreadySet] = useState<boolean>(false);
  const [isPhoneNumberAlreadySet, setIsPhoneNumberAlreadySet] = useState<boolean>(false);

  const [countryCode, setCountryCode] = useState("");
  const [birthdate, setBirthdate] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const userContext = useContext(UserContext);

  const [startDate, setStartDate] = useState<Date | undefined>();
  const [checkGeneralConditions, setCheckGeneralConditions] = useState(false);

  const userData = location.state?.properties || {};
  const nextStep = location?.state?.nextStep || "";
  const userContextData = userContext?.user || {};

  const {
    phoneCountryCode,
  } = userData;

  const [email, setEmail] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");

  const GENERAL_CONDITIONS = "faq12";
  const ALLOWED_AGE_TO_CREATE_CARD = 16;

  const handleDesktopDatePickerSelect = (e: any) => {
    clearErrors();
    const isoDateValue = e.target.value;

    if (isoDateValue) {
      const dateObj = new Date(isoDateValue);
      // Always format as DD/MM/YYYY using date-fns
      const displayDate = format(dateObj, 'dd/MM/yyyy');

      setBirthdate(displayDate);
      setLabelBirthdateVisible(true);
      setValue("birthdate", displayDate);
      setStartDate(dateObj);
    }
  };

  const formState = useForm<Form>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: {
      name: "",
    },
  });

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = formState;

  const handlePhoneNumberChange = useCallback(
    (input: string, phoneNumberCountryCode: string) => {
      setCountryCode(phoneNumberCountryCode);
      setPhoneNumber(input?.trim());
      clearErrors();
      setValue(
        "phoneNumber",
        input?.trim() !== "" ? `${phoneNumberCountryCode}${input}`.trim() : ""
      );
    },
    [clearErrors, setValue]
  );

  useEffect(() => {
    clearErrors();

    if (userData.email) {
      setEmail(userData.email)
      setValue("email", userData.email)

      setIsEmailAlreadySet(true);
    }

    if (userData.phoneNumber) {

      handlePhoneNumberChange(
        userData.phoneNumber,
        userData.phoneCountryCode || "+351"
      );

      setPhoneNumber(userData.phoneNumber);
      setValue("phoneNumber", userData.phoneNumber)
      setIsPhoneNumberAlreadySet(true);
    }

  }, []);

  const handleNameChange = ({
    target: { value },
  }: ChangeEvent<HTMLInputElement>) => {
    clearErrors();
    setValue("name", value.trim());
    setName(value);
  };

  const handleEmailChange = ({
    target: { value },
  }: ChangeEvent<HTMLInputElement>) => {
    clearErrors();
    setValue("email", value.trim());
    setEmail(value);
  };

  const handleCheckGeneralConditionsChange = () => {
    setValue("checkGeneralConditions", !checkGeneralConditions);
    setCheckGeneralConditions((state) => {
      return !state;
    });
  };

  const onSubmit: SubmitHandler<CardCreation> = async (data) => {
    setLoading(true);
    const { data: response, error } = await tryCatch(cardCreationService.cardCreation(
      {
        birthdate: data.birthdate,
        email: data.email,
        name: data.name,
        countryCode: phoneCountryCode || countryCode,
        phoneNumber: phoneNumber,
        checkGeneralConditions: checkGeneralConditions,
      },
      nextStep
    ));

    handleResponse(response, {
      setError
    }, error);

    setLoading(false);
  };

  useEffect(() => {
    const nameValidator = (register: UseFormRegister<Form>) =>
      register("name", {
        required: {
          value: true,
          message: t("common.required_field"),
        },
        pattern: {
          value: /^([a-zA-ZÀ-ú']{2,40}(\s+|$)){2,}$/,
          message: t("add_card.invalid_name"),
        },
      });
    const emailValidator = (register: UseFormRegister<Form>) =>
      register("email", {
        required: {
          value: true,
          message: t("common.required_field"),
        },
        pattern: {
          value: /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/,
          message: t("add_card.invalid_email"),
        },
      });
    const phoneNumberValidator = (register: UseFormRegister<Form>) =>
      register("phoneNumber", {
        required: {
          value: true,
          message: t("common.required_field"),
        },
        pattern: {
          value: /^\+?\d*$/,
          message: t("add_card.invalid_phonenumber"),
        },
        validate: {
          valid: (phoneNumber) =>
            validatePortuguesePhoneNumber(phoneNumber) ||
            t("common.error_invalid_pt_number"),
        },
      });
    const birthdateValidator = (register: UseFormRegister<Form>) =>
      register("birthdate", {
        required: {
          value: true,
          message: t("common.required_field"),
        },
        validate: {
          yearsOld: (v) =>
            getAge(v) >= ALLOWED_AGE_TO_CREATE_CARD ||
            t("add_card.invalid_age"),
          validDate: (v) => {
            if (!v) return true; // Required validation will handle empty

            try {
              // Use date-fns to validate the date
              const parsedDate = parse(v, 'dd/MM/yyyy', new Date());
              return isValid(parsedDate) && !isFuture(parsedDate) || t("add_card.invalid_date");
            } catch (error) {
              return t("add_card.invalid_date_format");
            }
          }
        },
      });

    const getAge = (inputBirthdate: string) => {
      if (!inputBirthdate) return 0;

      try {
        // Parse the date in DD/MM/YYYY format using date-fns
        const birthDate = parse(inputBirthdate, 'dd/MM/yyyy', new Date());

        // Validate the parsed date
        if (!isValid(birthDate) || isFuture(birthDate)) return 0;

        // Calculate age using date-fns
        return differenceInYears(new Date(), birthDate);
      } catch (error) {
        return 0;
      }
    };

    const checkGeneralConditionsValidator = (register: UseFormRegister<Form>) =>
      register("checkGeneralConditions", {
        required: {
          value: true,
          message: t("common.required_field"),
        },
      });
    nameValidator(register);
    emailValidator(register);
    phoneNumberValidator(register);
    birthdateValidator(register);
    checkGeneralConditionsValidator(register);
  }, [register, t]);

  return (
    <Form
      id="ContinenteCardCreation"
      form={formState}
      onSubmit={(e) => {
        clearErrors();
        handleSubmit(onSubmit, (e) => {
          console.log("invalido");
        })(e);
      }}
    >
      <Header
        title={t("add_card.header_title")}
        subtitle={t("add_card.header_message")}
        showLoading={loading}
        previousStepBackXTimes={1}
      />
      <Input
        id="name__input"
        autofocus
        ariaLabel={t("accessibility.loyaltycard.loyalty_card_creation.name_input")}
        labelText={t("add_card.name_input")}
        isError={errors.name ? true : false}
        errorMsg={errors.name ? errors.name.message : ""}
        onChange={handleNameChange}
        placeholder={t("add_card.name_placeholder")}
        value={name}
        required
      />
      <Input
        id="email__input"
        labelText={t("add_card.email_input")}
        isError={errors.email ? true : false}
        errorMsg={errors.email ? errors.email.message : ""}
        disabled={isEmailAlreadySet}
        ariaLabel={isEmailAlreadySet ?
          `${t("accessibility.loyaltycard.loyalty_card_creation.email_input.already_set")} ${email}` :
          t("accessibility.loyaltycard.loyalty_card_creation.email_input.not_set")
        }
        ariaReadonly={isEmailAlreadySet}
        ariaDisabled={isEmailAlreadySet}
        placeholder={t("add_card.email_placeholder")}
        onChange={handleEmailChange}
        value={email}
        autoComplete="on"
        required
        inputMode="email"
      />
      <UsernameInput
        type="phone"
        isError={!!(errors.phoneNumber || errors.blocked)}
        errorMsg={errors.phoneNumber?.message ||
          errors.blocked?.message ||
          ""}
        onChange={handlePhoneNumberChange}
        disabled={isPhoneNumberAlreadySet}
        ariaReadonly={isPhoneNumberAlreadySet}
        ariaLabel={isPhoneNumberAlreadySet ?
          `${t("accessibility.loyaltycard.loyalty_card_creation.phonenumber_input.already_set")} ${phoneNumber}` :
          t("accessibility.loyaltycard.loyalty_card_creation.phonenumber_input.not_set")}
        placeholder={t("add_card.phone_placeholder")}
        ariaDisabled={isPhoneNumberAlreadySet}
        autoComplete="off"
        value={phoneNumber}
        defaultValue={phoneNumber}
        defaultPhoneCountryCode={phoneCountryCode || "+351"}
        defaultCountryCode={"PT"}
        autocompleteWith={"phone"}
        required
      />
      <Datepicker
        id="birthdate__input"
        labelText={t("add_card.dob_input")}
        isFilled={labelBirthdateVisible}
        isError={errors.birthdate ? true : false}
        errorMsg={errors.birthdate ? errors.birthdate.message : ""}
        value={startDate}
        onChange={handleDesktopDatePickerSelect}
        required
        ariaLabel={startDate ? `${t("accessibility.loyaltycard.loyalty_card_creation.birthdate_input.already_set")} ${startDate.toLocaleDateString("pt-PT")}` : t("accessibility.loyaltycard.loyalty_card_creation.birthdate_input.not_set")}
      />
      <div className="flex">
        <Checkbox
          ariaLabel="Declaro que aceito os Termos e Condições e que li a Informação sobre o Tratamento de Dados Pessoais"
          onClick={handleCheckGeneralConditionsChange}
          isError={errors.checkGeneralConditions ? true : false}
        />
        <div className="terms-and-conditions-checkbox-div text-sm text-[var(--on-surface-default)]">
          <span aria-hidden className="text-sm ">
            {t("common.check_general_conditions_message1")}{" "}
          </span>
          <span
            role="link"
            onClick={() => goToFaq(GENERAL_CONDITIONS)}
            className="underline cursor-pointer text-[var(--on-surface-primary-default-variant)] hover:text-[var(--on-surface-primary-hover-variant)] pressed:text-[var(--on-surface-primary-pressed-variant)] disabled:text-[var(--on-surface-variant-2)]"
          >
            <b>{t("common.check_general_conditions_message2")}</b>
          </span>
        </div>
      </div>
      <ErrorMessage error={errors.submitError} />
      <Button
        type="submit"
        aria-label={checkGeneralConditions ? t("accessibility.loyaltycard.loyalty_card_creation.button.submit") : t("accessibility.loyaltycard.loyalty_card_creation.button.cant_submit")}
        disabled={!checkGeneralConditions}
      >
        {t("add_card.button_next_message")}
      </Button>
    </Form>
  );
}
